import FileUpload from '../components/FileUpload';
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Grid, Card, CardContent, Chip, Avatar, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { projectService } from '../services/api';
import api from '../services/api';
import Layout from '../components/Layout';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', address: '', city: '', state: '', zip_code: '', total_budget: '', start_date: '', end_date: '', image_url: '', gc_id: '', members: [] });
  const [gcUsers, setGcUsers] = useState([]);
  const [repUsers, setRepUsers] = useState([]);
  const [projectImages, setProjectImages] = useState([]);
  const [editDialog, setEditDialog] = useState({ open: false, project: null });
  const [editForm, setEditForm] = useState({});
  const [editImages, setEditImages] = useState([]);
  const [gcs, setGCs] = useState([]);
  const [assignGCDialog, setAssignGCDialog] = useState({ open: false, project: null });
  const [selectedGC, setSelectedGC] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [gcPerms, setGcPerms] = useState(null);
  useEffect(() => {
    if (user?.role === 'general_contractor') {
      api.get('/admin/settings').then(r => setGcPerms(r.data?.gc_permissions || {})).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    projectService.getAll().then(res => setProjects(res.data)).catch(() => {});
    api.get('/auth/users/by-role?roles=general_contractor').then(r => setGcUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/auth/users/by-role?roles=owner_representative,inspector').then(r => setRepUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const handleEdit = (project, e) => {
    e.stopPropagation();
    setEditForm({
      name: project.name || '',
      description: project.description || '',
      address: project.address || '',
      city: project.city || '',
      state: project.state || '',
      zip_code: project.zip_code || '',
      total_budget: project.total_budget || '',
      start_date: project.start_date ? project.start_date.split('T')[0] : '',
      end_date: project.end_date ? project.end_date.split('T')[0] : '',
      status: project.status || 'active',
      image_url: project.image_url || '',
    });
    setEditImages([]);
    setEditDialog({ open: true, project });
  };

  const handleAssignGC = async () => {
    try {
      await api.patch(`/projects/${assignGCDialog.project.id}/assign-gc`, { gc_id: selectedGC || null });
      projectService.getAll().then(res => setProjects(res.data));
      setAssignGCDialog({ open: false, project: null });
      setSelectedGC('');
    } catch (err) { console.error(err); }
  };

  const handleUpdate = async () => {
    try {
      const imageUrl = editImages[0] ? (editImages[0].url || editImages[0].gcsUrl || '') : editForm.image_url;
      await projectService.update(editDialog.project.id, { ...editForm, image_url: imageUrl });
      setEditDialog({ open: false, project: null });
      projectService.getAll().then(res => setProjects(res.data));
    } catch (err) { console.error(err); }
  };

  const handleCreate = async () => {
    if (!form.name || !form.address) {
      alert('Project name and address are required');
      return;
    }
    try {
      const imageUrl = projectImages[0] ? (projectImages[0].url || projectImages[0].gcsUrl || '') : '';
      const res = await projectService.create({ ...form, image_url: imageUrl });
      setProjects([res.data, ...projects]);
      setOpen(false);
      setForm({ name: '', description: '', address: '', city: '', state: '', zip_code: '', total_budget: '', start_date: '', end_date: '', image_url: '', gc_id: '', members: [] });
    setProjectImages([]);
    } catch (err) {
      alert('Failed to create project');
    }
  };

  const filtered = projects.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));

  const statusColor = { active: { bg: '#e8f5e9', text: '#2e7d32' }, completed: { bg: '#e3f2fd', text: '#1565c0' }, on_hold: { bg: '#fff3e0', text: '#e65100' }, cancelled: { bg: '#ffebee', text: '#c62828' } };

  return (
    <Layout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e' }}>Projects</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>{projects.length} total projects</Typography>
        </Box>
        {(user?.role !== 'general_contractor' || (gcPerms !== null && gcPerms.can_create_projects === true)) && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}
          sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>New Project</Button>}
      </Box>

      <TextField fullWidth placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)}
        sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment> }} />

      <Grid container spacing={3}>
        {filtered.map(project => (
          <Grid item xs={12} md={6} lg={4} key={project.id}>
            <Card elevation={0} sx={{ borderRadius: 3, cursor: 'pointer', overflow: 'hidden',
                transition: 'all 0.25s', '&:hover': { boxShadow: '0 12px 32px rgba(0,0,0,0.18)', transform: 'translateY(-3px)' } }}
              onClick={() => navigate(`/projects/${project.id}`)}>
              {/* Full photo or gradient background */}
              <Box sx={{ position: 'relative', height: 240, overflow: 'hidden' }}>
                {project.image_url ? (
                  <img src={project.image_url} alt={project.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                  <Box sx={{ width: '100%', height: '100%',
                    background: 'linear-gradient(135deg, #1a237e 0%, #283593 60%, #3949ab 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 80, fontWeight: 900, color: 'rgba(255,255,255,0.12)', letterSpacing: -4 }}>
                      {project.name?.[0]?.toUpperCase()}
                    </Typography>
                  </Box>
                )}
                {/* Dark gradient overlay at bottom */}
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)' }} />
                {/* Status badge top right */}
                <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                  <Chip label={project.status} size="small"
                    sx={{ bgcolor: statusColor[project.status]?.bg, color: statusColor[project.status]?.text,
                      fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }} />
                </Box>
                {/* Address overlay at bottom */}
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2 }}>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 700, fontSize: 13,
                    textTransform: 'uppercase', letterSpacing: 0.8, textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                    {project.address ? `${project.address}, ${project.city}, ${project.state}` : project.name}
                  </Typography>
                </Box>
              </Box>
              {/* Bottom info strip */}
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e' }}>{project.name}</Typography>
                  {project.description && (
                    <Typography variant="caption" sx={{ color: '#888', display: '-webkit-box',
                      WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {project.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Budget</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: '#43a047' }}>
                    ${Number(project.total_budget).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>Create New Project</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Project Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} margin="normal" required />
          <TextField fullWidth label="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} margin="normal" multiline rows={3} />
          <TextField fullWidth label="Address *" value={form.address} onChange={e => setForm({...form, address: e.target.value})} margin="normal" required error={!form.address} helperText={!form.address ? "Address is required" : ""} />
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="City *" value={form.city} onChange={e => setForm({...form, city: e.target.value})} margin="normal" required /></Grid>
            <Grid item xs={3}><TextField fullWidth label="State" value={form.state} onChange={e => setForm({...form, state: e.target.value})} margin="normal" /></Grid>
            <Grid item xs={3}><TextField fullWidth label="ZIP" value={form.zip_code} onChange={e => setForm({...form, zip_code: e.target.value})} margin="normal" /></Grid>
          </Grid>
          <TextField fullWidth label="Total Budget ($)" value={form.total_budget ? Number(form.total_budget).toLocaleString() : ''}
            onChange={e => {
              const raw = e.target.value.replace(/,/g, '');
              if (!isNaN(raw) || raw === '') setForm({...form, total_budget: raw});
            }}
            margin="normal"
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            placeholder="0" />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', mb: 1 }}>Project Photo (optional)</Typography>
            <FileUpload onFilesChange={setProjectImages} accept="image/*" />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="Start Date" type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="End Date" type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} /></Grid>
          </Grid>

          <TextField select fullWidth label="Assign General Contractor (optional)" value={form.gc_id}
            onChange={e => setForm({...form, gc_id: e.target.value})} margin="normal">
            <MenuItem value="">— No GC assigned —</MenuItem>
            {gcUsers.map(u => <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</MenuItem>)}
          </TextField>

          <TextField select fullWidth label="Add Owner Representative / Inspector (optional)"
            value="" onChange={e => {
              if (e.target.value && !form.members.find(m => m.user_id === e.target.value)) {
                const user = repUsers.find(u => u.id === e.target.value);
                setForm({...form, members: [...form.members, { user_id: e.target.value, role: user?.role || 'owner_representative' }]});
              }
            }} margin="normal">
            <MenuItem value="">— Select to add —</MenuItem>
            {repUsers.map(u => <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name} — {u.role?.replace(/_/g,' ')} ({u.email})</MenuItem>)}
          </TextField>
          {form.members.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {form.members.map(m => {
                const u = repUsers.find(x => x.id === m.user_id);
                return <Chip key={m.user_id} label={u ? u.first_name + ' ' + u.last_name : m.user_id}
                  onDelete={() => setForm({...form, members: form.members.filter(x => x.user_id !== m.user_id)})}
                  size="small" sx={{ bgcolor: '#e8eaf6', color: '#1a237e' }}/>;
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>Create Project</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, project: null })}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>Edit Project</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Project Name" value={editForm.name || ''} onChange={e => setEditForm({...editForm, name: e.target.value})} margin="normal" required />
          <TextField fullWidth label="Description" value={editForm.description || ''} onChange={e => setEditForm({...editForm, description: e.target.value})} margin="normal" multiline rows={3} />
          <TextField fullWidth label="Address" value={editForm.address || ''} onChange={e => setEditForm({...editForm, address: e.target.value})} margin="normal" />
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="City" value={editForm.city || ''} onChange={e => setEditForm({...editForm, city: e.target.value})} margin="normal" /></Grid>
            <Grid item xs={3}><TextField fullWidth label="State" value={editForm.state || ''} onChange={e => setEditForm({...editForm, state: e.target.value})} margin="normal" /></Grid>
            <Grid item xs={3}><TextField fullWidth label="ZIP" value={editForm.zip_code || ''} onChange={e => setEditForm({...editForm, zip_code: e.target.value})} margin="normal" /></Grid>
          </Grid>
          <TextField fullWidth label="Total Budget ($)" type="number" value={editForm.total_budget || ''} onChange={e => setEditForm({...editForm, total_budget: e.target.value})} margin="normal" />
          <TextField fullWidth select label="Status" value={editForm.status || 'active'} onChange={e => setEditForm({...editForm, status: e.target.value})} margin="normal">
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="on_hold">On Hold</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="Start Date" type="date" value={editForm.start_date || ''} onChange={e => setEditForm({...editForm, start_date: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="End Date" type="date" value={editForm.end_date || ''} onChange={e => setEditForm({...editForm, end_date: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} /></Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', mb: 1 }}>Project Photo</Typography>
            {editForm.image_url && (
              <Box sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden', height: 120 }}>
                <img src={editForm.image_url} alt="current" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
            )}
            <FileUpload onFilesChange={setEditImages} accept="image/*" />
            {editForm.image_url && <Typography variant="caption" sx={{ color: '#999' }}>Upload a new photo to replace the current one</Typography>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditDialog({ open: false, project: null })} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate} sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>Save Changes</Button>
        </DialogActions>
      </Dialog>
      {/* Assign GC Dialog */}
      <Dialog open={assignGCDialog.open} onClose={() => setAssignGCDialog({ open: false, project: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Assign General Contractor</DialogTitle>
        <DialogContent>
          <TextField select label="General Contractor" value={selectedGC} onChange={e => setSelectedGC(e.target.value)} fullWidth sx={{ mt: 1 }}>
            <MenuItem value="">— None —</MenuItem>
            {gcs.map(gc => <MenuItem key={gc.id} value={gc.id}>{gc.first_name} {gc.last_name} ({gc.email})</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignGCDialog({ open: false, project: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignGC}>Assign</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
